import React, { useState } from 'react';
import { StatusChipType, SystemIcon, Typography } from '@utmost.co/utmost-design-system';
import { SmartFormPermissionData, useSmartFormPermissionContext } from '../SmartformPermissionContext';
import { mapPolicyKeyToPermissionKey } from '../utils/useGetStatusType';
import { useGetPolicyDecision } from '../utils/useGetPolicyDecision';
import {
  StyledContainer,
  StyledHeader,
  StyledHeaderTypography,
  StyledLink,
  StyledLinkContainer,
  StyledLinkTypography,
  StyledNoPolicyTypography,
  StyledPill,
  TruncatedLink
} from './Styles.css/PolicyLink.styles';
import { PermissionLoadingSkeleton } from './PermissionLoadingSkeleton';
import { PermissionPill } from './PermissionPill';
import styled from 'styled-components';
import { FieldPermission } from '../../../forms';

interface PermissionHelperProps {
  permission?: FieldPermission;
  resourceUid?: string;
  permissionKey: 'READ' | 'UPDATE';
}

export const PermissionPolicyLink: React.FC<React.PropsWithChildren<PermissionHelperProps>> = ({
  permission,
  resourceUid,
  permissionKey
}) => {
  const [_, setOpen] = useState(false);
  const handleMenuClose = () => setOpen(false);

  const { goToPolicyDetails, goToPolicyVisualizer, hasPolicy, loading, canViewPolicyAnalyser, canViewPolicyDetails } =
    useSmartFormPermissionContext();

  const data: SmartFormPermissionData = {
    user: true,
    permissionName: permission?.PERMISSION_NAME,
    permissionType: permissionKey,
    resourceUid: resourceUid,
    isRedirected: true
  };

  const legacyKey = mapPolicyKeyToPermissionKey(permissionKey);

  const { data: policyData } = useGetPolicyDecision(
    permissionKey,
    permission?.PERMISSION_NAME || '',
    resourceUid || ''
  );

  const handleGoToPolicyDetails = () => {
    handleMenuClose();
    const policyUid = policyData?.decision?.by?.policy?.uid;
    if (policyUid) {
      goToPolicyDetails(policyUid);
    }
  };

  const handleInspect = () => {
    handleMenuClose();
    goToPolicyVisualizer(data);
  };

  return (
    <StyledContainer>
      {!!loading ? (
        <PermissionLoadingSkeleton />
      ) : (
        <StyledLinkContainer>
          <StyledHeader>
            <PermissionPill
              permission={permission}
              permissionKey={permissionKey}
              type={getPillType(permission, legacyKey)}
            />
            {renderPolicyText(hasPolicy, legacyKey, permission)}
            {canViewPolicyDetails ? (
              <TruncatedLink disabled={!canViewPolicyDetails} variant="label1" onClick={handleGoToPolicyDetails}>
                {policyData?.decision?.by?.policy?.title}
              </TruncatedLink>
            ) : (
              <Typography variant="label1">{policyData?.decision?.by?.policy?.title}</Typography>
            )}
            <Typography variant="body1">to</Typography>
            <StyledPill text={getPillText(permissionKey)} $type={getPillType(permission, legacyKey)} />
          </StyledHeader>
          {canViewPolicyAnalyser && permission?.[legacyKey as keyof FieldPermission] !== undefined && (
            <StyledLink>
              <StyledIcon name="open_in_new" size={14} />
              <StyledLinkTypography onClick={handleInspect} variant="body1">
                Inspect with Policy Analyser
              </StyledLinkTypography>
            </StyledLink>
          )}
        </StyledLinkContainer>
      )}
    </StyledContainer>
  );
};

const renderPolicyText = (hasPolicy: boolean, legacyKey: string, permission?: FieldPermission) => {
  if (hasPolicy && permission?.[legacyKey as keyof FieldPermission] !== undefined) {
    return <StyledHeaderTypography variant="body1">by policy</StyledHeaderTypography>;
  }
  return (
    <>
      <StyledHeaderTypography variant="body1">by policy</StyledHeaderTypography>
      <StyledNoPolicyTypography variant="label1">No associated policy</StyledNoPolicyTypography>
    </>
  );
};

const getPillType = (permission?: FieldPermission, permissionKey?: string) => {
  if (permission?.[permissionKey as keyof FieldPermission] !== undefined) {
    return !!permission?.[permissionKey as keyof FieldPermission]
      ? StatusChipType.LIGHT_GREEN
      : StatusChipType.LIGHT_RED;
  }
  return StatusChipType.GREY;
};

const getPillText = (permissionKey: 'READ' | 'UPDATE') => {
  return permissionKey === 'READ' ? 'Read' : 'Update';
};

const StyledIcon = styled(SystemIcon)`
  color: ${({ theme }) => theme.colorPrimary} !important;
`;
